import { useEffect, useRef, useState } from "react";
import { queryClient } from "../../App";
import Avatar from "../shared/Avatar";
import { useAuthStore } from "../../store/authStore";
import { AVATAR_BG_PRESETS, isAvatarBg, useSettingsStore } from "../../store/settingsStore";
import { createAvatarThumb, shouldUploadAsBase64 } from "./avatarImage.js";
import { avatarFileUrl, resolveAvatarUrl } from "./useAvatarSrc";
import { deleteUserAvatar, fetchAvatarPresign, updateUserSettings, uploadUserAvatar } from "../../utils/apiUtils";
import "./Settings.scss";

const initialsFromEmail = (email) => {
    const local = String(email || "").split("@")[0];
    const parts = local.split(/[._-]+/).filter(Boolean);
    if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return (parts[0] || "ME").slice(0, 2).toUpperCase();
};

const saveAvatarThumb = async (thumb) => {
    if (shouldUploadAsBase64(thumb.bytes)) {
        return uploadUserAvatar({ image: thumb.dataUrl });
    }
    const { url, s3Key } = await fetchAvatarPresign();
    const put = await fetch(url, {
        method: "PUT",
        body: thumb.blob,
        headers: { "Content-Type": thumb.contentType },
    });
    if (!put.ok) throw new Error("Could not upload photo");
    return uploadUserAvatar({ avatarKey: s3Key });
};

const Settings = () => {
    const { email } = useAuthStore();
    const avatarBg = useSettingsStore((state) => state.avatarBg);
    const avatarUrl = useSettingsStore((state) => state.avatarUrl);
    const avatarKey = useSettingsStore((state) => state.avatarKey);
    const setAvatarBg = useSettingsStore((state) => state.setAvatarBg);
    const setAvatar = useSettingsStore((state) => state.setAvatar);
    const photoSrc = avatarKey || avatarUrl ? avatarFileUrl(email) : "";
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const saveTimer = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const applyColor = (value) => {
        if (!isAvatarBg(value)) return;
        setAvatarBg(value);
        setError("");
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await updateUserSettings({ color: value.toLowerCase() });
            } catch (err) {
                setError(err?.message || "Could not save color");
            }
        }, 400);
    };

    const onPickPhoto = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setBusy(true);
        setError("");
        try {
            const saved = await saveAvatarThumb(await createAvatarThumb(file));
            setAvatar({ url: resolveAvatarUrl(saved.avatarUrl), key: saved.avatarKey });
            queryClient.invalidateQueries({ queryKey: ["avatar-url", String(email || "").toLowerCase()] });
            queryClient.invalidateQueries({ queryKey: ["access"] });
        } catch (err) {
            setError(err?.message || "Could not save photo");
        } finally {
            setBusy(false);
        }
    };

    const onRemovePhoto = async () => {
        setBusy(true);
        setError("");
        try {
            await deleteUserAvatar();
            setAvatar({ url: "", key: "" });
            queryClient.invalidateQueries({ queryKey: ["avatar-url", String(email || "").toLowerCase()] });
            queryClient.invalidateQueries({ queryKey: ["access"] });
        } catch (err) {
            setError(err?.message || "Could not remove photo");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="container settings scrollable">
            <p className="title">Settings</p>
            <p className="sub-title">Preferences for your workspace</p>

            <section className="settings__card">
                <div className="settings__card-head">
                    <button
                        type="button"
                        className="settings__preview"
                        onClick={() => fileRef.current?.click()}
                        disabled={busy}
                        aria-label="Add avatar photo"
                    >
                        <Avatar
                            src={photoSrc}
                            initials={initialsFromEmail(email)}
                            backgroundColor={avatarBg}
                            color="var(--white)"
                            dimension="56px"
                        />
                    </button>
                    <div>
                        <p className="settings__label">Avatar</p>
                        <div className="settings__photo-actions">
                            <button
                                type="button"
                                className="settings__btn"
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                            >
                                {photoSrc ? "Change photo" : "Add photo"}
                            </button>
                            {photoSrc ? (
                                <button
                                    type="button"
                                    className="settings__link"
                                    onClick={onRemovePhoto}
                                    disabled={busy}
                                >
                                    Remove
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={onPickPhoto}
                />

                <p className="settings__label">Background</p>
                <div className="settings__swatches" role="list">
                    {AVATAR_BG_PRESETS.map((swatch) => (
                        <button
                            key={swatch}
                            type="button"
                            role="listitem"
                            className={`settings__swatch${avatarBg === swatch ? " is-on" : ""}`}
                            style={{ background: swatch }}
                            aria-label={`Use ${swatch}`}
                            aria-pressed={avatarBg === swatch}
                            onClick={() => applyColor(swatch)}
                        />
                    ))}
                    <label className="settings__custom">
                        <span>Custom</span>
                        <input
                            type="color"
                            value={isAvatarBg(avatarBg) ? avatarBg : "#307da1"}
                            onChange={(event) => applyColor(event.target.value)}
                        />
                    </label>
                </div>
                {error ? <p className="danger">{error}</p> : null}
            </section>
        </div>
    );
};

export default Settings;
